import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { listerTableauxDeBord, type ResumeTableauDeBord } from '@/api/tableauxDeBord'
import { listerConteneurs, listerVues, type Conteneur, type Vue } from '@/api/vues'
import { realtime } from '@/api/realtime'
import { useApresReconnexion } from '@/composables/useApresReconnexion'
import { i18n } from '@/i18n'
import { arborescence, conteneurDe, ongletsDe, pagesDe, trouverPage, type Page, type RefPage } from '@/components/vues/arborescence'
import { iconeVue } from '@/components/vues/icones'
import { libelleDe } from '@/components/vues/format'
import type { EntreeNavigation } from '@/navigation'
import { useAuthStore } from './auth'

/**
 * Les PAGES de l'organisation — vues et tableaux de bord (0061) — et leurs
 * conteneurs : ce que la barre latérale affiche sous « Vues », et la
 * destination de l'écran d'accueil (la première).
 *
 * Quatre sources de rechargement, chacune nécessaire :
 *   * l'organisation EFFECTIVE change (connexion, jump backoffice, retour) —
 *     sinon la barre montrerait les vues de l'organisation précédente ;
 *   * un administrateur modifie la structure (événement `vues.updated`), la
 *     navigation (`vues.navigation` : conteneurs, rangement des sous-vues) ou
 *     un tableau de bord (`tableaux-de-bord.updated`)
 *     — sinon une vue créée n'apparaîtrait chez les collègues qu'au
 *     rechargement ;
 *   * le canal temps réel REVIENT après une coupure — les événements émis
 *     pendant la coupure sont perdus, une vue créée entre-temps serait manquée ;
 *   * un appel explicite (`recharger`) : bouton « Réessayer ».
 */
export const useVuesStore = defineStore('vues', () => {
  const auth = useAuthStore()

  const vues = ref<Vue[]>([])
  /** Conteneurs de vues : entrées de la barre dont les sous-vues sont des onglets. */
  const conteneurs = ref<Conteneur[]>([])
  /** Tableaux de bord que la session peut ouvrir : des pages, comme les vues. */
  const tableauxDeBord = ref<ResumeTableauDeBord[]>([])
  /** Message du dernier échec, `null` après un chargement réussi. Affiché par la barre. */
  const erreur = ref<string | null>(null)
  /**
   * Un chargement a-t-il ABOUTI (succès ou échec) pour l'organisation courante ?
   * Sans lui, « liste pas encore arrivée » et « organisation sans vue » ont la
   * même forme (`[]`) : l'accueil annoncerait l'absence de vue le temps d'une
   * requête, à chaque connexion.
   */
  const charge = ref(false)

  // Numéro de génération : une réponse lente de l'organisation PRÉCÉDENTE ne
  // doit pas écraser la liste de la nouvelle (jump suivi d'un retour rapide).
  let generation = 0

  async function recharger(): Promise<void> {
    if (!auth.user) {
      vues.value = []
      conteneurs.value = []
      tableauxDeBord.value = []
      return
    }
    const courante = ++generation
    try {
      // Les TROIS ou aucune : une liste de vues sans ses conteneurs remettrait
      // les sous-vues à la racine de la barre le temps d'une requête.
      const [reponseVues, reponseConteneurs, reponseBords] = await Promise.all([
        listerVues(),
        listerConteneurs(),
        listerTableauxDeBord(),
      ])
      if (courante !== generation) return
      vues.value = reponseVues.vues
      conteneurs.value = reponseConteneurs.conteneurs
      tableauxDeBord.value = reponseBords.tableauxDeBord
      erreur.value = null
    } catch (e) {
      if (courante !== generation) return
      erreur.value = e instanceof Error ? e.message : String(e)
    }
    charge.value = true
  }

  watch(
    () => auth.user?.organisationId ?? null,
    (organisation) => {
      generation++
      vues.value = []
      conteneurs.value = []
      tableauxDeBord.value = []
      erreur.value = null
      charge.value = false
      if (organisation !== null) void recharger()
    },
    { immediate: true },
  )

  // Abonnement pour la vie de l'application : le store est un singleton, et
  // le client temps réel se ré-abonne de lui-même après une reconnexion.
  realtime.subscribe('vues.updated', () => void recharger())
  realtime.subscribe('vues.navigation', () => void recharger())
  realtime.subscribe('tableaux-de-bord.updated', () => void recharger())

  useApresReconnexion(() => void recharger())

  const pages = computed(() => pagesDe(vues.value, tableauxDeBord.value))

  const cibleVue = (id: string) => ({ name: 'vue', params: { id } })
  const cible = (page: Page) =>
    page.genre === 'vue' ? cibleVue(page.vue.id) : { name: 'tableau-de-bord', params: { id: page.tableauDeBord.id } }
  const nomEntree = (page: Page) => (page.genre === 'vue' ? `vue:${page.vue.id}` : `tableau-de-bord:${page.tableauDeBord.id}`)
  const champsPage = (page: Page) => (page.genre === 'vue' ? page.vue : page.tableauDeBord)

  /**
   * Entrées de la barre latérale, dans la langue courante de l'interface. Un
   * conteneur ouvre son PREMIER onglet, et reste l'entrée active sur chacun
   * des autres (`aussiActifSur`).
   */
  const entreesNavigation = computed<EntreeNavigation[]>(() => {
    const langue = i18n.global.locale.value
    return arborescence(pages.value, conteneurs.value).map((entree): EntreeNavigation => {
      if (entree.genre !== 'conteneur') {
        const { icone, libelles } = champsPage(entree)
        return {
          nom: nomEntree(entree),
          groupe: 'vues',
          portee: 'utilisateur',
          icone: iconeVue(icone),
          libelle: libelleDe(libelles, langue),
          cible: cible(entree),
        }
      }
      const [premier, ...autres] = entree.onglets
      return {
        nom: `conteneur:${entree.conteneur.id}`,
        groupe: 'vues',
        portee: 'utilisateur',
        icone: iconeVue(entree.conteneur.icone),
        libelle: libelleDe(entree.conteneur.libelles, langue),
        // `arborescence` n'émet pas de conteneur sans onglet.
        cible: cible(premier!),
        aussiActifSur: autres.map(cible),
      }
    })
  })

  /** Conteneur d'une page, ou `null` pour une page de la barre latérale. */
  function conteneurDePage(ref: RefPage): Conteneur | null {
    return conteneurDe(pages.value, conteneurs.value, ref)
  }

  /** Conteneur d'une vue (écran des rôles). */
  function conteneurDeVue(vueId: string): Conteneur | null {
    return conteneurDePage({ genre: 'vue', id: vueId })
  }

  /** Onglets du conteneur d'une page (elle comprise), vides pour une page racine. */
  function ongletsDePage(ref: RefPage): Page[] {
    const conteneur = conteneurDePage(ref)
    return conteneur === null ? [] : ongletsDe(pages.value, conteneur.id)
  }

  /**
   * Nom d'ÉCRAN d'une page (titre du document, annonce de navigation) :
   * « Conteneur – Onglet » quand le conteneur montre plusieurs onglets, le seul
   * conteneur quand il n'en a qu'un (l'onglet n'est alors pas affiché), la page
   * sinon. `null` tant que la page n'est pas connue.
   */
  function nomEcran(ref: RefPage): string | null {
    const langue = i18n.global.locale.value
    const page = trouverPage(pages.value, ref)
    if (page === null) return null
    const nom = libelleDe(champsPage(page).libelles, langue)
    const conteneur = conteneurDePage(ref)
    if (conteneur === null) return nom
    const nomConteneur = libelleDe(conteneur.libelles, langue)
    return ongletsDePage(ref).length > 1 ? `${nomConteneur} – ${nom}` : nomConteneur
  }

  return {
    vues,
    conteneurs,
    tableauxDeBord,
    erreur,
    charge,
    recharger,
    entreesNavigation,
    conteneurDePage,
    conteneurDeVue,
    ongletsDePage,
    nomEcran,
  }
})
